/** My profile: akkaunt (admin boshqaradi), vaqt zonasi, bildirishnomalar, parol, 2FA (eskiz 7) */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { dayjs } from "@/lib/utils";
import { meKey, useMe } from "@/features/auth/hooks";
import type { Me } from "@/features/auth/types";
import { PageHeader, PageLoading, Kv } from "@/components/ui/misc";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Field, Input, Select } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Toggle } from "@/components/ui/toggle";
import { toast } from "@/components/ui/toast";
import { BackupCodes, TotpEnrollment, type TotpSetup } from "./TotpEnrollment";

const TIMEZONES = ["Asia/Tashkent", "America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "UTC"];

export function ProfilePage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Profile me={me.data} />;
}

function Profile({ me }: { me: Me }) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation();
  const qc = useQueryClient();
  const branchTz = me.branch?.timezone ?? "Asia/Tashkent";
  const [tz, setTz] = useState(me.timezone);
  const [notif, setNotif] = useState<Record<string, boolean>>({ in_app: true, ...me.notification_settings });

  const save = useMutation({
    mutationFn: (data: Partial<Me> & { version: number }) => api.patch<Me>("/api/auth/me/", data),
    onSuccess: (data) => {
      qc.setQueryData(meKey, data);
      toast.success(t("saved"));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : tc("toast.failed")),
  });

  return (
    <div>
      <PageHeader title={t("title")} subtitle={`${me.full_name} · ${tc(`roles.${me.role}`)}${me.branch ? ` · ${me.branch.name}` : ""}`} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardHeader title={t("account.title")} />
            <CardBody>
              <Kv label={t("account.fullName")}>{me.full_name}</Kv>
              <Kv label={t("account.username")}>
                {me.username} <Lock size={11} className="ml-1 inline text-muted" />
              </Kv>
              <Kv label={t("account.role")}>
                {tc(`roles.${me.role}`)} <Lock size={11} className="ml-1 inline text-muted" />
              </Kv>
              <Kv label={t("account.branch")}>{me.branch?.name ?? "—"}</Kv>
              <Kv label={t("account.manager")}>{me.manager_name ?? "—"}</Kv>
              <Kv label={t("account.memberSince")}>{dayjs(me.member_since).format("MMM YYYY")}</Kv>
              <p className="mt-2 text-[11px] text-muted">
                <Lock size={11} className="mr-1 inline" />
                {t("account.managed")}
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t("timezone.title")} />
            <CardBody>
              <Field label={t("timezone.myTime")} hint={t("timezone.branchHint", { tz: branchTz })}>
                <Select value={tz} onChange={(e) => setTz(e.target.value)}>
                  {[...new Set([branchTz, ...TIMEZONES])].map((z) => (
                    <option key={z} value={z}>
                      {z} (UTC{dayjs().tz(z).format("Z")})
                    </option>
                  ))}
                </Select>
              </Field>
              <p className="mt-2 text-[12px] text-muted">{t("timezone.driverTimes")}</p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t("notifications.title")} />
            <CardBody className="space-y-3">
              <Toggle label={t("notifications.inApp")} checked={notif.in_app ?? true} onChange={(v) => setNotif({ ...notif, in_app: v })} />
              <Toggle label={t("notifications.telegram")} checked={false} disabled onChange={() => {}} hint={t("notifications.telegramHint")} />
              <div className="pt-1">
                <Button variant="primary" loading={save.isPending} onClick={() => save.mutate({ timezone: tz === branchTz ? "" : tz, notification_settings: notif, version: me.version })}>
                  {tc("actions.saveChanges")}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <ChangePassword />
          <TwoFactor me={me} />
        </div>
      </div>
    </div>
  );
}

function ChangePassword() {
  const { t } = useTranslation("profile");
  const [cur, setCur] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const change = useMutation({
    mutationFn: () => api.post("/api/auth/password/change/", { current_password: cur, new_password: p1 }),
    onSuccess: () => {
      toast.success(t("password.updated"));
      setCur("");
      setP1("");
      setP2("");
      setErrors({});
    },
    onError: (e) => {
      if (e instanceof ApiError && e.fields) setErrors(e.fields);
      else toast.error(e instanceof ApiError ? e.message : t("password.failed"));
    },
  });
  const mismatch = p2 !== "" && p1 !== p2;
  return (
    <Card>
      <CardHeader title={t("password.title")} />
      <CardBody>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!mismatch) change.mutate();
          }}
        >
          <Field label={t("password.current")} error={errors.current_password}>
            <Input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("password.new")} error={errors.new_password}>
              <Input type="password" autoComplete="new-password" value={p1} onChange={(e) => setP1(e.target.value)} />
            </Field>
            <Field label={t("password.confirm")} error={mismatch ? t("password.mismatch") : undefined}>
              <Input type="password" autoComplete="new-password" value={p2} onChange={(e) => setP2(e.target.value)} />
            </Field>
          </div>
          <p className="text-[11px] text-muted">{t("password.hint")}</p>
          <Button type="submit" variant="primary" loading={change.isPending} disabled={!cur || !p1 || mismatch}>
            {t("password.update")}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function TwoFactor({ me }: { me: Me }) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation();
  const qc = useQueryClient();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  // Har yangi sozlash -- yangi secret: QR rasmi qayta so'raladi (brauzer eski rasmni ko'rsatmasin)
  const [qrVersion, setQrVersion] = useState(0);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [password, setPassword] = useState("");

  const start = useMutation({
    mutationFn: () => api.post<TotpSetup>("/api/auth/2fa/setup/"),
    onSuccess: (res) => {
      setSetup(res);
      setQrVersion((v) => v + 1);
    },
  });
  const disable = useMutation({
    mutationFn: () => api.post<{ user: Me }>("/api/auth/2fa/disable/", { password }),
    onSuccess: (res) => {
      qc.setQueryData(meKey, res.user);
      setDisableOpen(false);
      setPassword("");
      toast.success(t("twofa.disabledToast"));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t("twofa.disableFailed")),
  });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {t("twofa.title")}
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${me.totp_enabled ? "bg-success-soft text-success" : "bg-fill"}`}>{me.totp_enabled ? tc("states.on") : tc("states.off")}</span>
          </span>
        }
        action={
          me.totp_enabled ? (
            // Rol siyosati (Settings -> Security) majburiy qilgan bo'lsa -- o'chirib bo'lmaydi (backend ham rad etadi)
            me.twofa_required ? (
              <span className="text-[11px] text-muted">{t("twofa.requiredRole")}</span>
            ) : (
              <Button size="sm" onClick={() => setDisableOpen(true)}>
                {t("twofa.disable")}
              </Button>
            )
          ) : (
            !setup && (
              <Button size="sm" variant="primary" loading={start.isPending} onClick={() => start.mutate()}>
                {t("twofa.enable")}
              </Button>
            )
          )
        }
      />
      <CardBody>
        {me.role === "owner" && !me.totp_enabled && !me.twofa_required && (
          <div className="mb-3 rounded-[10px] border border-budget bg-warning-soft px-3 py-2 text-[12px]">{t("twofa.ownerAdvice")}</div>
        )}
        {!me.totp_enabled && !setup && !codes && (
          <p className="text-[13px] text-ink-soft">{t("twofa.intro")}</p>
        )}
        {setup && (
          <TotpEnrollment
            setup={setup}
            qrVersion={qrVersion}
            onEnabled={(res) => {
              qc.setQueryData(meKey, res.user);
              setCodes(res.backup_codes);
              setSetup(null);
              toast.success(t("twofa.enabledToast"));
            }}
            onCancel={() => setSetup(null)}
          />
        )}
        {codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} />}
        {me.totp_enabled && !codes && (
          <>
            <p className="text-[13px] text-ink-soft">{t("twofa.enabledText")}</p>
            {/* Yangi telefon: o'chirib qayta yoqish; majburiy rolda -- tiklash (owner) yoki rol belgisini olish (owner o'zi) */}
            <p className="mt-1 text-[12px] text-muted">
              {t(!me.twofa_required ? "twofa.newPhone" : me.role === "owner" ? "twofa.newPhoneOwner" : "twofa.newPhoneAskOwner")}
            </p>
          </>
        )}
      </CardBody>
      <Modal
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title={t("twofa.disableTitle")}
        description={t("twofa.disableDesc")}
        footer={
          <>
            <Button onClick={() => setDisableOpen(false)}>{tc("actions.cancel")}</Button>
            <Button variant="danger" loading={disable.isPending} onClick={() => disable.mutate()} disabled={!password}>
              {t("twofa.disable")}
            </Button>
          </>
        }
      >
        <Field label={t("twofa.password")}>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
        </Field>
      </Modal>
    </Card>
  );
}
